/**
 * @packageDocumentation
 * @category Component
 */

import 'BomdisoftComponents/buttons/styles.css';
import { type ButtonColor, type ButtonSize, type ButtonVariant, toButtonColor } from 'BomdisoftComponents/buttons/render';
import { joinClasses } from 'BomdisoftComponents/html/index';
import type { ComponentProps, ReactNode } from 'react';

export type { ButtonColor, ButtonSize, ButtonVariant } from 'BomdisoftComponents/buttons/render';

type ButtonBase = ComponentProps<'button'> & {
  endIcon?: ReactNode;
  fullWidth?: boolean;
  size?: ButtonSize;
  startIcon?: ReactNode;
};

/**
 * `outlined` takes a `color` and honours it in every state, reversing the
 * 2026-08-23 decision that made it neutral-only (user, 2026-08-29).
 *
 * That decision was taken because outlined accepted a tone and dropped all of
 * it but the focus ring, so nine destructive buttons shipped neutral. Rejecting
 * the prop stopped the silent miss but did not give those buttons their tone,
 * and products reached for the obvious workaround: a local class overriding
 * `color` and `border-color` only, which reproduced the original defect one
 * layer up as a red button with a neutral hover.
 *
 * Now the tone drives label, border, hover and ring together. `outlined`
 * defaults to `neutral` rather than `info`, so every button that never asked
 * for a tone renders exactly as it did before.
 */
export type ButtonProps = ButtonBase & { color?: ButtonColor; variant?: ButtonVariant };

export const Button = ({ children, className, color, endIcon, fullWidth, size = 'medium', startIcon, type = 'button', variant = 'text', ...rest }: ButtonProps) => (
  <button className={joinClasses('bomdisoft-button', className)} data-color={toButtonColor(color, variant)} data-full-width={fullWidth ? '' : undefined} data-size={size} data-variant={variant} type={type} {...rest}>
    {startIcon && <span className='bomdisoft-button-start-icon'>{startIcon}</span>}
    {children}
    {endIcon && <span className='bomdisoft-button-end-icon'>{endIcon}</span>}
  </button>
);
